<template>
  <div class="whats-app-client">
    <modal-dialog
      title="Выберите чат для пересылки"
      v-bind:value="forwardMessageDialog"
      class="whats-app-client__modal"
    >
      <div class="whats-app-client__modal__list">
        <label
          v-for="(chat, index) in chatList"
          v-bind:key="`forward-chat-${index}`"
          class="whats-app-client__modal__radio"
        >
          <input
            name="forward-chat-id"
            v-bind:value="chat.id"
            v-on:change="selectChatToForward(chat.id)"
            type="radio"
          >
          <span>{{ chat.name }}</span>
        </label>
      </div>
      <button
        type="button"
        v-on:click="sendForwardMessage"
        class="whats-app-client__modal__button"
      >
        Отправить
      </button>
    </modal-dialog>
    <whats-app-chat
      class="whats-app-client__chat"
      v-if="selectedChat"
      v-bind:name="selectedChat.name"
      v-bind:initial-messages="selectedChat.messages"
      v-on:close="onCloseChat"
      v-on:close-client="onClose"
      v-on:send-message="onSendMessage"
      v-on:delete-message="onDeleteMessage"
      v-on:forward-message="onForwardMessage"
    />
    <div class="whats-app-client__contacts">
      <h3 class="whats-app-client__caption">Контакты</h3>
      <input-text-search
        class="patient-visits__services__search-input"
        placeholder="Введите имя, номер..."
        v-model="searchQuery"
      />
      <div
        class="whats-app-client__list"
        v-if="hasChats"
      >
        <button
          v-for="(chat, index) in chatList"
          class="whats-app-client__list__item"
          type="button"
          v-bind:class="{
            active: chat.id === selectedChatId,
            notification: chat.hasNotification === true,
          }"
          v-bind:key="`chat-${index}`"
          v-bind:disabled="chat.id === selectedChatId"
          v-on:click="onOpenChat(chat.id)"
        >
          {{ chat.name }}
        </button>
      </div>
      <div v-else>
        <div class="whats-app-client__text">
          Чатов не обнаружено
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WhatsAppChat from '@/components/WhatsAppChat.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import debounce from 'lodash.debounce';
import InputTextSearch from '@/components/InputTextSearch.vue';

/**
 * @description Название события открытия чата.
 * @type {string}
 */
const NAME_EVENT_OPEN_CHAT = 'open-chat';

/**
 * @description Название события отправки сообщения.
 * @type {string}
 */
const NAME_EVENT_SEND_MESSAGE = 'send-message';

/**
 * @description Название события удаления сообщения.
 * @type {string}
 */
const NAME_EVENT_DELETE_MESSAGE = 'delete-message';

/**
 * @description Название события пересылки сообщения.
 * @type {string}
 */
const NAME_EVENT_FORWARD_MESSAGE = 'forward-message';

/**
 * @description Название события закрытия чата.
 * @type {string}
 */
const NAME_EVENT_CLOSE_CHAT = 'close-chat';

/**
 * @description Название события закрытия меню Whats App.
 * @type {string}
 */
const NAME_EVENT_CLOSE = 'close';

/**
 * @description Название события поиска.
 * @type {string}
 */
const NAME_EVENT_SEARCH = 'search';

/**
 * @description Компонент Whats App клиента.
 * @type {Vue}
 */
export default {
  components: {
    InputTextSearch,
    ModalDialog,
    WhatsAppChat,
  },
  data() {
    return {
      phoneNewChat: '',
      forwardMessageDialog: false,
      forwardFrom: '',
      forwardTo: '',
      forwardMessage: '',
      searchQuery: '',
    };
  },
  name: 'WhatsAppClient',
  methods: {
    /**
     * @description Обработать событие "Поиск"
     * @param {string} searchQuery
     */
    fetchMatchedChats(searchQuery) {
      this.$emit(NAME_EVENT_SEARCH, searchQuery);
    },
    /**
     * @description Обработать событие "закрыть чат"
     * @returns {void}
     */
    onCloseChat() {
      this.$emit(NAME_EVENT_CLOSE_CHAT);
    },
    /**
     * @description Событие удаления сообщения.
     * @param {string} messageId
     */
    onDeleteMessage(messageId) {
      this.$emit(NAME_EVENT_DELETE_MESSAGE, messageId, this.selectedChat.id);
    },
    /**
     * @description Событие обработки пересылки сообщения.
     * @param {string} messageId
     */
    onForwardMessage(messageId) {
      this.forwardFrom = this.selectedChat.id;
      this.forwardMessage = messageId;
      this.forwardMessageDialog = true;
    },
    /**
     * @description Событие пересылки сообщения.
     */
    sendForwardMessage() {
      this.forwardMessageDialog = false;
      this.$emit(NAME_EVENT_FORWARD_MESSAGE, this.forwardTo, this.forwardFrom, this.forwardMessage);
    },
    /**
     * @description Событие открытия чата.
     * @param {string} chatId
     * @returns {void}
     */
    onOpenChat(chatId) {
      this.$emit(NAME_EVENT_OPEN_CHAT, chatId);
    },
    /**
     * @description Событие отправки сообщения.
     * @param {string} text
     * @param {string} quotedMessageId
     * @param {Object} file
     * @returns {void}
     */
    onSendMessage(text, quotedMessageId, file) {
      this.$emit(NAME_EVENT_SEND_MESSAGE, text, this.selectedChat.id, quotedMessageId, file);
    },
    /**
     * @description События закрытия меню.
     * @returns {void}
     */
    onClose() {
      this.$emit(NAME_EVENT_CLOSE);
    },
    /**
     * @description Выбрать чат для пересылки.
     * @param {string} chatId
     */
    selectChatToForward(chatId) {
      this.forwardTo = chatId;
    },
  },
  props: {
    /**
     * @description Список чатов.
     * @type {Array}
     */
    chatList: {
      required: true,
      type: Array,
    },
    /**
     * @description Наличие чатов.
     * @type {Boolean}
     */
    hasChats: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Начальный выбранный чат.
     * @type {Object}
     */
    selectedChat: {
      default: () => null,
      type: Object,
    },
    /**
     * @description Идентификатор выбранного чата.
     * @type {String}
     */
    selectedChatId: {
      default: () => null,
      type: String,
    },
    /**
     * @description Ожидание загрузки найденных чатов.
     * @type {Boolean}
     */
    awaitLoadingMatchedChats: {
      default: () => false,
      type: Boolean,
    },
  },
  watch: {
    searchQuery: {
      handler: debounce(function _(value) {
        this.fetchMatchedChats(value);
      }, 300),
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.whats-app-client {
  display: flex;
  flex-direction: row;
  background-color: #fff;
  height: 100%;
  &__modal {
    &__list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin: 10px 0 30px 0;
    }
    &__button {
      @include buttons.btn-blue(100%, 50px, 16px);
    }
    &__radio > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    &__radio > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    &__radio > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    &__radio > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    &__radio > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
  &__caption {
    font-size: 24px;
    line-height: 28px;
    font-weight: 600;
    margin: 0 0 30px 0;
    min-width: 300px;
  }
  &__text {
    font-size: 16px;
    line-height: 18px;
    font-weight: 500;
    margin: 50px 0 0 0;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 2;
    position: relative;
    background-color: white;
    margin: 0 -27px 0 -27px;
    padding: 18px 30px 30px 27px;
    box-shadow: -6px 0px 18px rgba(0, 0, 0, 0.06);
    overflow-y: auto;
    height: 95%;
    &__item {
      display: flex;
      align-items: center;
      border: 1px solid #DBDBDB;
      border-radius: 4px;
      padding: 13px 18px;
      font-size: 15px;
      line-height: 18px;
      font-weight: 500;
      text-align: left;
      color: #192A3E;
      width: 400px;
      &:before {
        content: '';
        width: 7px;
        min-width: 7px;
        height: 7px;
        min-height: 7px;
        border-radius: 50%;
        margin: 0 10px 0 0;
      }
      &:hover {
        background-color: #EAEFF3;
        border: 1px solid #EAEFF3;
      }
      &.active {
        background-color: #EAEFF3;
        border: 1px solid #EAEFF3;
      }
      &.notification {
        background-color: #F4F8EE;
        &:before {
          background-color: #29A34B;
        }
      }
    }
  }
  &__contacts {
    width: 418px;
  }
}
@media screen and (max-width: 860px) {
  .whats-app-client {
    &__list {
      margin: 0;
      height: 85%;
      box-shadow: none;
      padding: 20px 0 30px;
    }
    &__contacts {
      padding: 22px 27px 50px;
      width: 100%;
    }
    &__chat +.whats-app-client__contacts {
      display: none;
    }
  }
}
@media screen and (max-width: 480px) {
  .whats-app-client {
    &__list {
      &__item {
        width: 100%;
      }
    }
  }
}
</style>
